import React from "react";
import { useLanguage } from "../context/LanguageContext";
import { useTheme } from "../context/ThemeContext";
import { useInView } from "react-intersection-observer";
import { useSpring, animated } from "@react-spring/web";
import chatbot from '/src/assets/examples/chatbot.webp';
import document from '/src/assets/examples/document.webp';
import automation from '/src/assets/examples/process.webp';
import email from '/src/assets/examples/email.webp';
import insight from '/src/assets/examples/data.webp';
import report from '/src/assets/examples/reporting.webp';

interface ExampleSectionProps {
  title: string;
  description: string;
  imageUrl: string;
  isReversed?: boolean;
  theme: string;
}

const ExampleSection: React.FC<ExampleSectionProps> = ({
  title,
  description,
  imageUrl,
  isReversed = false,
  theme
}) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const isDark = theme === "dark";

  const animation = useSpring({
    from: { 
      opacity: 0, 
      transform: 'translateY(50px)'
    },
    to: { 
      opacity: inView ? 1 : 0, 
      transform: inView ? 'translateY(0px)' : 'translateY(50px)'
    },
    config: { tension: 120, friction: 14 },
    delay: 200,
  });

  return (
    <animated.div 
      ref={ref} 
      style={animation} 
      className={`
        w-full
        rounded-2xl
        border-2
        overflow-hidden
        group
        transition-all
        duration-500
        hover:-translate-y-1
        ${isDark 
          ? "bg-black/80 border-green-500/30 hover:border-green-400" 
          : "bg-white/80 border-green-900/30 hover:border-green-800"
        }
      `}
    >
      <div className="flex flex-col h-full">
        {/* Image Section */}
        <div className="relative h-48 overflow-hidden">
          <div className={`
            absolute inset-0 
            bg-gradient-to-b 
            z-10
            ${isDark 
              ? "from-transparent via-black/50 to-black" 
              : "from-transparent via-white/50 to-white"
            }
          `} />
          <img
            src={imageUrl}
            alt={title}
            className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
          />
        </div>

        {/* Content Section */}
        <div className="p-6 flex flex-col flex-grow">
          <h3 className={`text-xl md:text-2xl lg:text-3xl font-bold mb-4 ${
            isDark ? "text-green-400" : "text-green-900"
          }`}>
            {title}
          </h3>
          <p className={`text-lg leading-relaxed ${
            isDark ? "text-gray-300" : "text-gray-700"
          }`}>
            {description}
          </p>
          
          {/* Hover Indicator */}
          <div className={`
            mt-6
            w-12 h-1 
            rounded-full 
            transition-all 
            duration-300
            group-hover:w-24
            ${isDark 
              ? "bg-gradient-to-r from-green-500/50 to-green-300/50" 
              : "bg-gradient-to-r from-green-900/50 to-green-700/50"
            }
          `} />
        </div>
      </div>
    </animated.div>
  );
};

const Examples = () => {
  const { language } = useLanguage();
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const content = {
    section1: {
      title: language === "en" 
        ? "Omnichannel AI Chatbots" 
        : "Chatbots IA Omnicanaux",
      description: language === "en"
        ? "Deploy voice and text chatbots on your existing customer touchpoints - phone systems, WhatsApp, Telegram, Slack, and more. Reduce response times by 70% while maintaining 24/7 availability."
        : "Déployez des chatbots vocaux et textuels sur vos canaux existants - téléphone, WhatsApp, Telegram, Slack, etc. Réduisez les temps de réponse de 70% avec une disponibilité 24/7.",
      imageUrl: chatbot
    },
    section2: {
      title: language === "en" 
        ? "Smart Document Intelligence" 
        : "Intelligence Documentaire",
      description: language === "en"
        ? "Transform lengthy documents into actionable summaries in seconds using our military-grade NLP. Perfect for legal teams reviewing contracts or executives needing boardroom-ready insights."
        : "Transformez des documents volumineux en résumés exploitables en secondes grâce à notre NLP de niveau militaire. Idéal pour les équipes juridiques ou les décideurs.",
      imageUrl: document
    },
    section3: {
      title: language === "en" 
        ? "Process Automation" 
        : "Automatisation des Processus",
      description: language === "en"
        ? "We automate repetitive tasks killing your productivity - data entry, inventory reconciliations, compliance checks. Our solutions adapt as your business evolves. Save 300+ hours/year per employee."
        : "Nous automatisons les tâches répétitives qui tuent votre productivité. Nos solutions évoluent avec vous. Gain typique : 300+ heures/an par employé.",
      imageUrl: automation
    },
    section4: {
      title: language === "en" 
        ? "Smart Email Management" 
        : "Gestion Intelligente d'Emails",
      description: language === "en"
        ? "Our AI understands email intent. Priority issues get flagged instantly, while routine requests auto-resolve using your knowledge base. 92% reduction in email overload reported."
        : "Notre IA comprend l'intention des emails. Les problèmes prioritaires sont traités immédiatement. 92% de réduction de la surcharge email rapportée.",
      imageUrl: email
    },
    section5: {
      title: language === "en" 
        ? "Customer Insights" 
        : "Insights Clients",
      description: language === "en"
        ? "Transform support tickets, surveys, and call transcripts into growth strategies. Detect emerging issues before they trend and identify upsell opportunities from customer feedback."
        : "Transformez les tickets support, enquêtes et appels en stratégies de croissance. Détectez les problèmes émergents et identifiez les opportunités de vente.",
      imageUrl: insight
    },
    section6: {
      title: language === "en" 
        ? "Executive Reporting" 
        : "Reporting Exécutif",
      description: language === "en"
        ? "Generate board-ready reports in one click. Integrates with 150+ data sources to deliver clear insights with visual highlights. Make decisions using fresh, not fossilized data."
        : "Générez des rapports exécutifs en un clic. Intégration avec 150+ sources pour des insights clairs avec visualisations. Décidez avec des données fraîches.",
      imageUrl: report
    }
  };

  return (
    <section 
      id="examples"
      className="relative py-32 px-8 overflow-hidden">
      <div className="max-w-[1600px] mx-auto">
        {/* Title */}
        <div className="text-center mb-32">
          <h2 className={`
            text-4xl md:text-5xl lg:text-6xl 
            font-bold 
            ${isDark 
              ? "bg-gradient-to-r from-green-400 via-green-500 to-green-400" 
              : "bg-gradient-to-r from-green-900 via-green-800 to-green-900"
            }
            bg-clip-text 
            text-transparent
            mb-6
            animate-gradient-x
          `}
          style={{ lineHeight: '1.2', paddingBottom: '10px' }}>
            {language === "en" ? "Some Examples" : "Quelques Exemples"}
          </h2>
          <div className={`w-24 h-1 mx-auto rounded-full mt-6 ${
            isDark 
              ? "bg-gradient-to-r from-green-500/50 to-green-300/50" 
              : "bg-gradient-to-r from-green-900/50 to-green-700/50"
          }`} />
        </div>

        {/* New Grid Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {Object.values(content).map((section, index) => (
            <ExampleSection
              key={index}
              title={section.title}
              description={section.description}
              imageUrl={section.imageUrl}
              isReversed={index % 2 !== 0}
              theme={theme}
            />
          ))}
        </div>
      </div>

      {/* Background decorative elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className={`absolute top-1/4 right-1/4 w-96 h-96 rounded-full blur-3xl ${
          isDark ? "bg-green-500/5" : "bg-green-900/5"
        }`} />
        <div className={`absolute bottom-1/4 left-1/4 w-96 h-96 rounded-full blur-3xl ${
          isDark ? "bg-green-500/5" : "bg-green-900/5"
        }`} />
      </div>
    </section>
  );
};

export default Examples; 